import { createSignal, untrack, For, Show } from 'solid-js';
import { createStore } from 'solid-js/store';
import { parseNumericInput } from '../lib/money.js';

// RekapAbsensi — the attendance review surface for a draft run.
//
// It derives every employee's period attendance from the fingerprint scans
// (the PROTOCOL, not the old rubber-stamp), shows the ~6 aggregate numbers
// per employee already filled in, and surfaces only the exceptions the admin
// must actually decide: "lupa scan" (NeedsReview) and "tanpa sidik jari".
// One click pushes the reviewed numbers into the run's input grid. Each cell
// stays editable — an override is just a normal input edit that wins.

async function getJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || ('HTTP ' + res.status));
  return res.json();
}

async function postJSON(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data };
}

// Status → badge tone for the daily drill-down.
const STATUS_TONE = {
  Valid: 'success',
  Alpha: 'danger',
  BelumFingerMasuk: 'warning',
  BelumFingerPulang: 'warning',
  HadirDefault: 'info',
};
const statusTone = (s) => STATUS_TONE[s] ?? 'info';

export default function RekapAbsensi(props) {
  // props: runId (number), onApplied (fn) — refetch the run detail after push.
  const [payload, setPayload] = createStore({ loaded: false, columns: [], rows: [] });
  const [drill, setDrill] = createStore({}); // nik → { open, loading, agg }
  const [loading, setLoading] = createSignal(false);
  const [applying, setApplying] = createSignal(false);
  // One-shot guard: after a successful apply the button stays disabled so an
  // accidental second click can't re-overwrite manual per-period edits. The
  // state is stored ON THE RUN (pay_run.attendance_applied_at, migration 047)
  // so it holds across tabs/browsers; Muat ulang explicitly unlocks it, and
  // overrides re-arm it server-side (they change the derived totals). A local
  // cell edit re-arms only this tab — the server then asks for confirmation.
  const [applied, setApplied] = createSignal(false);
  const [msg, setMsg] = createSignal(null);
  const [onlyReview, setOnlyReview] = createSignal(false);
  const [dept, setDept] = createSignal('');
  const [search, setSearch] = createSignal('');
  let gridWrap; // ref to the scroll container, so a reload can restore its scroll

  async function load() {
    setLoading(true);
    setMsg(null);
    try {
      const d = await getJSON(`/api/run/${props.runId}/attendance`);
      setPayload({ loaded: true, columns: d.columns ?? [], rows: d.rows ?? [], isShift: !!d.isShift, viaProc: d.viaProc || '' });
      setApplied(!!d.appliedAt); // server truth — consistent across tabs
      setDrill({});
    } catch (e) {
      setMsg({ ok: false, msg: e.message });
    }
    setLoading(false);
  }

  function setCell(i, code, v) {
    setPayload('rows', i, 'values', code, v);
    setApplied(false); // edited values differ from what was applied
  }

  async function toggleDrill(nik) {
    const cur = drill[nik];
    if (cur?.open) { setDrill(nik, 'open', false); return; }
    if (cur?.agg) { setDrill(nik, 'open', true); return; }
    setDrill(nik, { open: true, loading: true, agg: null });
    try {
      const agg = await getJSON(`/api/run/${props.runId}/attendance/${encodeURIComponent(nik)}`);
      setDrill(nik, { open: true, loading: false, agg });
    } catch (e) {
      setDrill(nik, { open: true, loading: false, agg: null, error: e.message });
    }
  }

  async function apply() {
    setApplying(true);
    setMsg(null);
    const cells = [];
    for (const row of payload.rows) {
      for (const c of payload.columns) {
        cells.push({ nik: row.nik, code: c.code, value: String(row.values[c.code] ?? '') });
      }
    }
    let r = await postJSON(`/api/run/${props.runId}/attendance/apply`, { cells });
    // Another tab already applied (server-side guard): offer a deliberate
    // unlock + retry instead of a dead-end error.
    if (!r.ok && r.data.alreadyApplied
        && confirm('Nilai sudah pernah diterapkan (mungkin dari tab lain). Terapkan lagi dan timpa nilai di input per periode?')) {
      await postJSON(`/api/run/${props.runId}/attendance/unlock`, {});
      r = await postJSON(`/api/run/${props.runId}/attendance/apply`, { cells });
    }
    setApplying(false);
    if (r.ok) {
      setApplied(true);
      setMsg({ ok: true, msg: `${r.data.written} nilai absensi dikirim ke input per periode.` });
      props.onApplied?.();
    } else setMsg({ ok: false, msg: r.data.error });
  }

  // Muat ulang: explicitly re-arm the server-side guard, then reload.
  async function muatUlang() {
    await postJSON(`/api/run/${props.runId}/attendance/unlock`, {});
    load();
  }

  // ── Manual hadir/tidak overrides ──────────────────────────────────────
  // Re-fetch the derived aggregates after an override. The grid is keyed off
  // visibleRows() (fresh wrapper objects each run), so updating payload.rows
  // rebuilds the rows — which is what lets the editable cells show the
  // re-derived numbers (their value is a plain, non-reactive prop). That rebuild
  // would otherwise scroll the grid to the top, so we capture the scroll
  // position and restore it right after — synchronously, so there's no flash.
  async function reloadRows() {
    try {
      const d = await getJSON(`/api/run/${props.runId}/attendance`);
      // The drill-down lives INSIDE the scrollable grid (.pg-grid-wrap), so an
      // override clicked deep in a day-table must restore BOTH the page scroll
      // and the grid's own scrollTop — otherwise rebuilding the rows jumps the
      // grid back to the top.
      const sc = document.scrollingElement;
      const y = sc ? sc.scrollTop : 0;
      const gy = gridWrap ? gridWrap.scrollTop : 0;
      setPayload('columns', d.columns ?? []);
      setPayload('rows', d.rows ?? []);
      setPayload('isShift', !!d.isShift);
      setPayload('viaProc', d.viaProc || '');
      setApplied(!!d.appliedAt); // overrides cleared the server-side guard
      if (sc) sc.scrollTop = y;
      if (gridWrap) gridWrap.scrollTop = gy;
    } catch (e) { /* keep current rows */ }
  }
  async function reloadDrill(nik) {
    try {
      const agg = await getJSON(`/api/run/${props.runId}/attendance/${encodeURIComponent(nik)}`);
      setDrill(nik, { open: true, loading: false, agg });
    } catch (e) { /* keep */ }
  }
  async function saveOverride(nik, date, present, reason, keterangan) {
    const r = await postJSON(`/api/run/${props.runId}/override`, { nik, date, present, reason, keterangan });
    if (!r.ok) { setMsg({ ok: false, msg: r.data.error }); return; }
    await reloadDrill(nik); await reloadRows();
  }
  async function clearOverride(nik, date) {
    const res = await fetch(`/api/run/${props.runId}/override?nik=${encodeURIComponent(nik)}&date=${date}`, { method: 'DELETE' });
    if (res.ok) { await reloadDrill(nik); await reloadRows(); }
  }
  async function bulkOverride(scope, nik) {
    const who = scope === 'all' ? 'SEMUA karyawan' : nik;
    if (!confirm(`Tandai semua hari kerja yang belum hadir sebagai HADIR untuk ${who}?\n(hari tanpa scan diberi alasan "Manual" — bisa disesuaikan per hari)`)) return;
    const r = await postJSON(`/api/run/${props.runId}/override/bulk`, { scope, nik });
    if (!r.ok) { setMsg({ ok: false, msg: r.data.error }); return; }
    setMsg({ ok: true, msg: `${r.data.marked} hari ditandai hadir.` });
    await reloadRows();
    for (const k of Object.keys(drill)) if (drill[k]?.open) await reloadDrill(k);
  }

  const viaProc = () => payload.viaProc || '';
  const reviewTotal = () => payload.rows.reduce((n, r) => n + (r.needsReview || 0), 0);
  const noFidCount = () => payload.rows.filter((r) => r.noFid).length;
  // Fresh wrapper objects each run: rebuilding the rows is what refreshes the
  // editable cells (their value is a plain, non-reactive prop) after an override.
  // reloadRows() restores the scroll position so the rebuild doesn't jump. Each
  // wrapper keeps the ORIGINAL payload index i, so setCell(i,…) stays correct
  // even after sorting.
  const departments = () => {
    const s = new Set();
    for (const r of payload.rows) if (r.department) s.add(r.department);
    return [...s].sort();
  };
  const rowMatches = (r, kw) =>
    (r.name || '').toLowerCase().includes(kw) || (r.nik || '').toLowerCase().includes(kw);
  const visibleRows = () => {
    const d = dept(), kw = search().trim().toLowerCase();
    return payload.rows
      .map((r, i) => ({ r, i }))
      .filter(({ r }) =>
        (!onlyReview() || r.needsReview > 0 || r.noFid) &&
        (!d || r.department === d) &&
        (!kw || rowMatches(r, kw)));
  };

  // ── Sorting (click a header) ──────────────────────────────────────────
  const [sort, setSort] = createSignal({ key: '', dir: 'asc' });
  function toggleSort(key) {
    const s = sort();
    setSort(s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  }
  const arrow = (key) => (sort().key === key ? (sort().dir === 'asc' ? ' ▲' : ' ▼') : '');
  function sortVal(r, key) {
    if (key === 'mode') return r.mode || '';
    if (key === 'review') return r.needsReview || 0;
    if (key.startsWith('val:')) return parseFloat(r.values[key.slice(4)]) || 0;
    return r.name || ''; // 'name'
  }
  // The comparator reads r.values, but cells commit on every keystroke — so the
  // value reads run UNTRACKED, or typing in a sorted value column would re-sort
  // and make the row jump. A header click or a row reload still re-sorts.
  const sortedRows = () => {
    const arr = visibleRows();
    const s = sort();
    if (!s.key) return arr;
    const mul = s.dir === 'asc' ? 1 : -1;
    const copy = [...arr];
    untrack(() => copy.sort((a, b) => {
      const av = sortVal(a.r, s.key), bv = sortVal(b.r, s.key);
      return (typeof av === 'number' && typeof bv === 'number')
        ? (av - bv) * mul : String(av).localeCompare(String(bv)) * mul;
    }));
    return copy;
  };

  return (
    <div class="card card-pad ab-rekap">
      <div class="ky-vars-head">
        <h4 class="kg-hist-title">Rekap Absensi</h4>
        <span class="kg-meta-text" style="margin-left:8px">
          {viaProc() ? `dihitung oleh stored procedure: ${viaProc()}` : 'dihitung dari scan sidik jari, bukan ditandai manual'}
        </span>
        <div class="spacer" />
        <Show when={!payload.loaded}>
          <button class="btn primary" disabled={loading()} onClick={load}>
            {loading() ? 'Menarik…' : 'Tarik & tinjau'}
          </button>
        </Show>
        <Show when={payload.loaded}>
          <button class="btn ghost" disabled={loading()} onClick={muatUlang}>{loading() ? 'Memuat…' : 'Muat ulang'}</button>
        </Show>
      </div>

      <p class="ky-vars-note">
        <Show when={viaProc()} fallback={
          <>Setiap angka diturunkan dari scan jari sesuai mode karyawan (MesinFinger
          butuh masuk &amp; pulang; MesinFingerPagi cukup pagi; Manual/NotApplicable
          hadir bawaan). Tinjau pengecualian, sesuaikan bila perlu, lalu kirim ke
          input per periode.</>
        }>
          Nilai dihitung oleh stored procedure <b>{viaProc()}</b> (dipatok pada run ini),
          bukan dari scan sidik jari. Tinjau, sesuaikan bila perlu, lalu kirim ke input
          per periode. Rincian harian &amp; tandai-hadir tidak berlaku untuk derivasi proc.
        </Show>
      </p>

      <Show when={msg()}>
        <div class={`kg-verdict ${msg().ok ? 'ok' : 'fail'}`} style="margin:8px 0">{msg().msg}</div>
      </Show>

      <Show when={payload.loaded}>
        <div class="ab-rekap-summary kg-meta-text">
          <span>{payload.rows.length} karyawan</span>
          <Show when={reviewTotal() > 0}>
            <span class="badge warning">{reviewTotal()} hari perlu ditinjau (lupa scan)</span>
          </Show>
          <Show when={noFidCount() > 0}>
            <span class="badge danger">{noFidCount()} tanpa sidik jari</span>
          </Show>
          <div class="spacer" />
          <Show when={!viaProc()}>
            <button class="btn ghost" title="Tandai semua hari kerja yang belum hadir sebagai hadir, untuk semua karyawan"
              onClick={() => bulkOverride('all', '')}>✓ Tandai semua hadir</button>
            <label class="ky-check">
              <input type="checkbox" checked={onlyReview()} onInput={(e) => setOnlyReview(e.currentTarget.checked)} />
              hanya yang perlu ditinjau
            </label>
          </Show>
        </div>

        <div class="lb-filter">
          <label class="kg-field"><span>Departemen</span>
            <select class="kg-select" value={dept()} onInput={(e) => setDept(e.currentTarget.value)}>
              <option value="">Semua departemen</option>
              <For each={departments()}>{(d) => <option value={d}>{d}</option>}</For>
            </select>
          </label>
          <label class="kg-field lb-search"><span>Cari nama / NIK</span>
            <input type="text" placeholder="kata kunci…" value={search()} onInput={(e) => setSearch(e.currentTarget.value)} />
          </label>
          <Show when={dept() || search()}>
            <button class="btn ghost" onClick={() => { setDept(''); setSearch(''); }}>Reset</button>
          </Show>
        </div>

        <div class="pg-grid-wrap" ref={gridWrap}>
          <table class="data pg-grid">
            <thead>
              <tr>
                <th class="pg-sticky sortable" onClick={() => toggleSort('name')}>Karyawan{arrow('name')}</th>
                <th class="sortable" onClick={() => toggleSort('mode')}>Mode{arrow('mode')}</th>
                <For each={payload.columns}>{(c) =>
                  <th class="num sortable" title={c.label} onClick={() => toggleSort('val:' + c.code)}>{c.code}{arrow('val:' + c.code)}</th>}
                </For>
                <th class="num sortable" onClick={() => toggleSort('review')}>Tinjau{arrow('review')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <For each={sortedRows()}>{({ r, i }) =>
                <>
                  <tr classList={{ 'ab-row-review': r.needsReview > 0 || r.noFid, 'ab-row-sticky': drill[r.nik]?.open }}>
                    <td class="pg-sticky">
                      <div class="kg-code">{r.nik}</div>
                      <div class="kg-sub">{r.name}</div>
                    </td>
                    <td><span class="badge info ab-mode">{r.mode}</span></td>
                    <For each={payload.columns}>{(c) =>
                      <td class="num">
                        <RekapCell value={r.values[c.code] ?? ''} onSave={(v) => setCell(i, c.code, v)} />
                      </td>
                    }</For>
                    <td class="num">
                      <Show when={r.needsReview > 0} fallback={<span class="vg-dim">—</span>}>
                        <span class="badge warning">{r.needsReview}</span>
                      </Show>
                      <Show when={r.noFid}><span class="badge danger" title="Karyawan ini belum punya nomor sidik jari (FID)">no FID</span></Show>
                    </td>
                    <td class="num">
                      <Show when={!viaProc()}>
                        <button class="btn ghost" onClick={() => toggleDrill(r.nik)}>
                          {drill[r.nik]?.open ? 'tutup' : 'rincian'}
                        </button>
                      </Show>
                    </td>
                  </tr>
                  <Show when={drill[r.nik]?.open}>
                    <tr class="ab-drill-row">
                      <td colspan={payload.columns.length + 4}>
                        <Show when={drill[r.nik]?.loading}><span class="kg-meta-text">memuat rincian…</span></Show>
                        <Show when={drill[r.nik]?.error}><span class="ferr">⚠ {drill[r.nik].error}</span></Show>
                        <Show when={drill[r.nik]?.agg}>
                          <DayTable agg={drill[r.nik].agg} nik={r.nik} mode={r.mode} isShift={payload.isShift}
                            onSet={(date, present, reason, ket) => saveOverride(r.nik, date, present, reason, ket)}
                            onClear={(date) => clearOverride(r.nik, date)}
                            onBulk={() => bulkOverride('person', r.nik)} />
                        </Show>
                      </td>
                    </tr>
                  </Show>
                </>
              }</For>
            </tbody>
          </table>
        </div>

        <div class="ab-rekap-foot">
          <span class="kg-meta-text">Angka tersimpan ke kolom input per periode di bawah; tetap bisa diubah manual.</span>
          <div class="spacer" />
          <button class="btn primary" disabled={applying() || applied()} onClick={apply}
            title={applied() ? 'Sudah diterapkan — klik Muat ulang untuk mengaktifkan kembali' : undefined}>
            {applying() ? 'Mengirim…' : applied() ? 'Sudah diterapkan ✓' : 'Terapkan ke input per periode'}
          </button>
        </div>
      </Show>
    </div>
  );
}

// RekapCell — editable numeric cell; commits on blur/Enter when changed. After
// an override, reloadRows() rebuilds the rows (visibleRows yields fresh wrappers)
// so this cell remounts with the re-derived value — the project's JSX transform
// doesn't make props reactive, so a remount is what refreshes the shown number.
function RekapCell(props) {
  const [val, setVal] = createSignal(props.value);
  return (
    <input
      class="pg-cell pg-cell-num"
      classList={{ filled: val() !== '' && val() !== '0' }}
      type="text"
      inputmode="decimal"
      value={val()}
      onInput={(e) => { setVal(e.currentTarget.value); props.onSave(parseNumericInput(e.currentTarget.value)); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
    />
  );
}

const FIXED_REASONS = ['Izin', 'Sakit', 'Cuti', 'Tugas', 'Manual']; // why a no-scan day is hadir
const SHIFT_REASONS = ['Pagi', 'Siang', 'Sore', 'Malam'];           // which shift to credit

// DayTable — the day-by-day evidence + the admin's hadir/tidak override.
// props: agg, nik, mode, isShift, onSet(date,present,reason,ket), onClear(date), onBulk().
function DayTable(props) {
  const fp = () => props.mode === 'MesinFinger' || props.mode === 'MesinFingerPagi';
  const reasons = () => props.isShift ? SHIFT_REASONS : FIXED_REASONS;
  const defReason = () => props.isShift ? 'Pagi' : 'Manual';
  // a "no scan" day: rolling-shift has no clock-in; fixed has no raw tap at all
  const noScan = (d) => props.isShift ? !d.clockIn : (fp() && !d.firstScan);

  function toggle(d, checked) {
    if (!checked) { props.onSet(d.date, false, '', ''); return; }            // force tidak hadir
    if (noScan(d)) { props.onSet(d.date, true, defReason(), ''); return; }   // present, needs reason/shift
    props.onSet(d.date, true, '', '');                                       // present (has scans)
  }

  return (
    <>
      <div style="display:flex;align-items:center;gap:8px;margin:4px 0 8px">
        <span class="kg-meta-text">Centang <b>Hadir</b> untuk menandai/membatalkan hari sebagai masuk kerja (mengganti hasil otomatis).</span>
        <div class="spacer" />
        <button class="btn ghost" onClick={() => props.onBulk()}>✓ Tandai semua hari kerja hadir</button>
      </div>
      <table class="data ab-days">
        <thead>
          <tr>
            <th>Tanggal</th><th>Hari</th><th>Jenis</th><th>Status</th>
            <th>Masuk</th><th>Pulang</th><th>Scan awal</th><th>Scan akhir</th>
            <th>Hadir</th><th>Penjelasan</th>
          </tr>
        </thead>
        <tbody>
          <For each={props.agg.days ?? []}>{(d) =>
            <tr classList={{ 'ab-day-review': d.needsReview, 'ab-day-off': d.dayType !== 'kerja' }}>
              <td class="ky-nik">{d.date}</td>
              <td>{d.weekday}</td>
              <td class="kg-meta-text">{d.dayType}</td>
              <td><span class={`badge ${statusTone(d.status)}`}>{d.leave || d.status}</span></td>
              <td class="ky-nik">{d.clockIn || '—'}</td>
              <td class="ky-nik">{d.clockOut || '—'}</td>
              <td class="ky-nik kg-meta-text">{d.firstScan || '—'}</td>
              <td class="ky-nik kg-meta-text">{d.lastScan || '—'}</td>
              <td>
                <Show when={d.dayType !== 'minggu'}>
                  <span style="display:inline-flex;align-items:center;gap:6px;flex-wrap:wrap">
                    <input type="checkbox" checked={d.present} onChange={(e) => toggle(d, e.currentTarget.checked)} />
                    <Show when={d.late}><span class="badge warning">telat</span></Show>
                    <Show when={d.overridden}>
                      <span class="badge info">manual</span>
                      <button class="btn ghost" style="padding:0 6px" title="Kembalikan ke hasil otomatis" onClick={() => props.onClear(d.date)}>↺</button>
                    </Show>
                    <Show when={d.present && noScan(d)}>
                      <select class="kg-select" style="width:auto;min-width:90px" value={d.reason || defReason()}
                        onChange={(e) => props.onSet(d.date, true, e.currentTarget.value, (!props.isShift && e.currentTarget.value === 'Manual') ? (d.keterangan || '') : '')}>
                        <For each={reasons()}>{(rs) => <option value={rs}>{rs}</option>}</For>
                      </select>
                      <Show when={!props.isShift && (d.reason || 'Manual') === 'Manual'}>
                        <input type="text" placeholder="keterangan" value={d.keterangan || ''} style="width:130px"
                          onChange={(e) => props.onSet(d.date, true, 'Manual', e.currentTarget.value)} />
                      </Show>
                    </Show>
                  </span>
                </Show>
              </td>
              <td class="kg-meta-text">{d.explain}</td>
            </tr>
          }</For>
        </tbody>
      </table>
    </>
  );
}
